import { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Login from './components/Login';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import StudentSearch from './components/TopMenu/Students/StudentSearch.js';
import AdultSearch from './components/TopMenu/AdultSearch.js';
import Residents from './components/TopMenu/Residents.js';
import TransfersPage from './components/Transfers/jsx/TransfersPage.jsx';
import { SERVER_URL, RECOVERY_USER_EMAIL } from './constants.js';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import Rent from './components/Lists/Rent.js';
import ClassesModule from './components/Classes/ClassesModule.js';
//import Activities from './components/Groups Activities/Activities.js';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LandingPage, { canOpenView, isPlainClick, viewHref } from './LandingPage';
import { can, getGrants, getRole, rememberLogin, forgetLogin } from './components/shared utils/auth';
import {
  clearDurable,
  durableEmail,
  installAuthInterceptor,
  isAnotherUsersSession,
  isDurable,
  restoreSession,
  watchSession,
} from './components/shared utils/session';
import HomeIcon from '@mui/icons-material/Home';
import { IconButton, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import OutstandingBalances from './components/Lists/OutstandingBalances.js';
import Allergies from './components/Lists/Allergies.js';
import DailySummaryPage from './components/DailySummary/DailySummaryPage.jsx';
import VersionChecker from './VersionChecker.js';
import ActivitiesPage from './components/Activities/ActivitiesPage.jsx';
import DailyRotaView from './components/DailyRota/DailyRotaView.jsx';
import StaffHoursPage from './components/StaffHours/StaffHoursPage.jsx';
//import ActivityListPage from './components/Lists/ActivityListPage.js';
import GroupsPage from './components/Booking Groups/GroupsPage.jsx';
import BookingGanttPage from './components/BookingGantt/BookingGanttPage.jsx';
import ProductsPage from './components/Products/ProductsPage.js';
import TransportPage from './components/Transport/jsx/TransportPage.jsx';
import TimetablePage from './components/Timetable/jsx/TimetablePage.jsx';
import StaffSchedulingView from './components/Timetable/jsx/StaffSchedulingView.jsx';
import BedroomsModule from './components/Bedrooms/components/BedroomsModule.jsx';
import dayjs from "dayjs";
import { useTheme, useMediaQuery } from "@mui/material";
import AdminPage from './components/AdminPage/AdminPage.jsx';
import QuotesPage from './components/Quotes/QuotesPage.jsx';
import AgentsPage from './components/Agents/AgentsPage.jsx';
import BirthdayCalendarPage from './components/Birthdays/BirthdayCalendarPage.jsx';
import HelpPage from './components/Help/HelpPage.jsx';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { jsonOr } from "./utils/http";

// The year selector runs from the first season on record to next year, so the
// upcoming season can be planned before it starts and the list never has to be
// edited again. Built once at load — nobody keeps the app open across New Year.
const FIRST_YEAR = 2023;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: Math.max(CURRENT_YEAR + 1 - FIRST_YEAR + 1, 1) },
  (_, i) => String(FIRST_YEAR + i)
);

// "Back to …" label shown in Staff Scheduling for each view it can be opened from.
const STAFF_SCHEDULE_BACK_LABELS = {
  classes: 'Back to manage classes',
  timetable: 'Back to timetable',
  activities: 'Back to activities plan',
};

// TV display accounts use the convention <campus>.tv@... (e.g. dollar.tv@bliss.com).
// They log straight into a locked, fullscreen timetable TV view for their campus.
// Returns the campus slug ("dollar") or null for a normal account.
function tvKioskCampusSlug(email) {
  const local = String(email || '').split('@')[0];
  const match = local.match(/^(.+)\.tv$/i);
  return match ? match[1] : null;
}

function App() {
  const [isAuthenticated, setAuth] = useState(false);
  // A view asked for by the address — someone opened a tile in a new tab, or
  // pasted a link. Held until they are signed in, since a new tab may have to
  // log in first. Reading it is all that happens here: React runs a state
  // initialiser twice in development, so tidying the address away from inside
  // one loses the view on the second pass.
  const [requestedView, setRequestedView] = useState(
    () => new URLSearchParams(window.location.search).get('view')
  );
  // A help page asked for by the address (?view=help&page=bedrooms), read
  // alongside the view for the same reason.
  const [helpSlug] = useState(
    () => new URLSearchParams(window.location.search).get('page')
  );
  // Only while a stored session is being rebuilt, so a returning user sees
  // nothing rather than the login form followed by the app.
  const [restoring, setRestoring] = useState(() => isDurable());
  // Financial visibility is the FINANCE grant (or ADMIN) — see can() in the
  // auth helper, which is where anything new should read it. This prop is the
  // older way of threading the same answer through the tree; it comes out of
  // components as they are touched, not in one sweep.
  const [showFinancials, setShowFinancials] = useState(() => can('finance'));
  const [selectedYear, setSelectedYear] = useState(String(CURRENT_YEAR));
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedAdultId, setSelectedAdultId] = useState(null);
  const [activeView, setActiveView] = useState('landing');
  const [globalDate, setGlobalDate] = useState(dayjs());
  const [globalCampusId, setGlobalCampusId] = useState("");
  const [activitiesSlot, setActivitiesSlot] = useState("EVENING");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const [recentAnchorEl, setRecentAnchorEl] = useState(null);
  const [recentStudents, setRecentStudents] = useState([]);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [staffScheduleReturnView, setStaffScheduleReturnView] = useState(null);

  // Unsaved-changes guard for the bedrooms allocator. The module registers
  // `{ dirty, save }` here; any attempt to leave the bedrooms view while dirty
  // is intercepted and a Save / Discard / Cancel dialog is shown instead.
  const bedroomsNavRef = useRef(null);
  const [pendingNav, setPendingNav] = useState(null); // thunk to run once resolved

  // The ref is only populated while the bedrooms allocator is mounted, so a
  // non-null `dirty` flag already implies we're on that view.
  const guardedNavigate = (proceed) => {
    if (bedroomsNavRef.current?.dirty) {
      setPendingNav(() => proceed);
    } else {
      proceed();
    }
  };

  const handlePendingNavSave = async () => {
    const proceed = pendingNav;
    try {
      const res = await bedroomsNavRef.current?.save?.();
      // Over-capacity or stale board: the save returns a conflict sentinel (not
      // a throw) — the Bedrooms module opens its resolution dialog, or reloads
      // and explains. Close this prompt but stay in Bedrooms so that's visible.
      if (res?.conflict) {
        setPendingNav(null);
        return;
      }
      setPendingNav(null);
      proceed?.();
    } catch {
      // Save failed validation (toast already shown) — stay put.
    }
  };

  const handlePendingNavDiscard = () => {
    const proceed = pendingNav;
    setPendingNav(null);
    proceed?.();
  };

  // Trap the browser Back button while signed in. The app has no in-app history
  // (state-based routing), so Back would otherwise unload the SPA and drop the
  // session. Seed a history entry and, on Back, stay in the app — returning to
  // landing — routed through the unsaved-changes guard.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    window.history.pushState({ app: true }, '');
    const onPopState = () => {
      // Re-seed so Back always has a buffer and never exits the app.
      window.history.pushState({ app: true }, '');
      const goLanding = () => {
        setSelectedStudentId(null);
        setSelectedAdultId(null);
        setActiveView('landing');
      };
      if (bedroomsNavRef.current?.dirty) {
        setPendingNav(() => goLanding);
      } else {
        goLanding();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeView !== 'classes' && activeView !== 'staff_schedule') {
      setStaffScheduleReturnView(null);
    }
  }, [activeView]);

  const fetchRecentStudents = useCallback(() => {
    const token = sessionStorage.getItem('bearer');
    fetch(`${SERVER_URL}api/audit/recent-students`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (jsonOr(res, [])))
      .then((data) => setRecentStudents(Array.isArray(data) ? data : []))
      .catch(() => setRecentStudents([]));
  }, []);

  const handleRecentOpen = (event) => {
    fetchRecentStudents();
    setRecentAnchorEl(event.currentTarget);
  };

  const handleRecentClose = () => {
    setRecentAnchorEl(null);
  };

  const handleRecentSelect = (studentId) => {
    handleRecentClose();
    handleSelectStudent(studentId);
  };

  const switchView = (view) => {
    guardedNavigate(() => {
      setSelectedStudentId(null);
      setSelectedAdultId(null);
      setActiveView(view);
    });
  };

  // "Keep me signed in" leaves a refresh token behind; on the next visit it
  // buys a fresh pair, and the role and grants come back from the server
  // rather than from anything the browser kept. Authority is never restored
  // from storage — only re-derived.
  useEffect(() => {
    if (!isDurable()) return;
    let cancelled = false;
    restoreSession()
      .then((data) => {
        if (cancelled) return;
        if (data?.access_token) {
          const email = durableEmail() || sessionStorage.getItem('userEmail') || '';
          if (data.role) {
            rememberLogin({
              email,
              role: data.role,
              grants: data.grants,
              showFinancials: data.showFinancials === true,
            });
          }
          setShowFinancials(can('finance'));
          setAuth(true);
          setActiveView('landing');
          if (tvKioskCampusSlug(email)) setGlobalDate(dayjs());
        }
      })
      .finally(() => {
        if (!cancelled) setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !requestedView) return;
    // Refused rather than rendered blank: a link to something this account
    // may not open lands on the home page instead of an empty screen.
    if (canOpenView(requestedView) && requestedView !== 'landing') {
      setActiveView(requestedView);
    }
    setRequestedView(null);
    // Tidied away only once it has been acted on, so it cannot go stale as
    // they navigate — and so a reload before signing in still honours it.
    window.history.replaceState({}, '', window.location.pathname);
  }, [isAuthenticated, requestedView]);

  const signOut = useCallback(() => {
    // Asked before forgetting, since forgetting is what it reads: when
    // someone else has signed in on this device, the durable session is
    // theirs and this tab only ends itself.
    const theirs = isAnotherUsersSession();
    forgetLogin();
    if (!theirs) clearDurable();
    setShowFinancials(false);
    setAuth(false);
  }, []);

  // A tab in use keeps its token current rather than discovering it is dead
  // on the next request. When the session reaches the age its client allows,
  // renewal is refused and there is nothing to do but show the login form.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return watchSession(signOut);
  }, [isAuthenticated, signOut]);

  // A request refused because the session ended — an admin signing someone
  // out, or the session reaching its cap — used to leave the screens up and
  // the calls failing. Installed once, for every request the app makes.
  useEffect(() => installAuthInterceptor(signOut), [signOut]);

  const onLoginSuccess = ({ email, role, grants, showFinancials: flag }) => {
    rememberLogin({ email, role, grants, showFinancials: flag });
    setShowFinancials(can('finance'));
    setAuth(true);
    setActiveView('landing');
    // TV display accounts always open on today's date.
    if (tvKioskCampusSlug(email)) setGlobalDate(dayjs());
  };

  const handleGoHome = () => {
    guardedNavigate(() => setActiveView('landing'));
  };

  const handleLandingSelect = (key) => {
    setActiveView(key);
  };

  const handleSelectStudent = (studentId) => {
    guardedNavigate(() => {
      setActiveView('students');
      setSelectedStudentId(studentId);
    });
  };

  const handleSelectAdult = (adultId) => {
    guardedNavigate(() => {
      setActiveView('adults');
      setSelectedAdultId(adultId);
    });
  };

  const handleLogout = () => {
    guardedNavigate(() => {
      const token = sessionStorage.getItem('bearer');
      fetch(`${SERVER_URL}api/v1/auth/logout`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) => console.error(err));

      forgetLogin();
      clearDurable();
      setShowFinancials(false);
      setAuth(false);
    });
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleOpenBedrooms = (date, campusId) => {
    if (date) setGlobalDate(dayjs(date));
    if (campusId !== undefined) setGlobalCampusId(campusId);

    setActiveView('bedrooms');
  };

  const handleOpenTransfers = (date) => {
    if (date) setGlobalDate(dayjs(date));
    setActiveView("transfers");
  };

  const handleOpenTransport = (date, campusId) => {
    if (date) setGlobalDate(dayjs(date));
    if (campusId !== undefined) setGlobalCampusId(campusId);

    setActiveView("transport");
  };

  const handleOpenClasses = (date, campusId) => {
    if (date) setGlobalDate(dayjs(date));
    if (campusId !== undefined) setGlobalCampusId(campusId);

    setActiveView("classes");
  };

  const handleOpenActivities = (date, campusId, slot) => {
    if (date) setGlobalDate(dayjs(date));
    if (campusId !== undefined) setGlobalCampusId(campusId);
    if (slot) setActivitiesSlot(slot);

    setActiveView("activities");
  };

  const handleOpenStaffSchedule = (date, campusId, fromView = 'timetable') => {
    if (date) setGlobalDate(dayjs(date));
    if (campusId !== undefined) setGlobalCampusId(campusId);

    setStaffScheduleReturnView(fromView);
    setActiveView('staff_schedule');
  };

  // TV display kiosk: a logged-in <campus>.tv@ account gets ONLY a locked,
  // fullscreen timetable TV view for its campus — no menu, no other views.
  const kioskCampusSlug = isAuthenticated
    ? tvKioskCampusSlug(sessionStorage.getItem('userEmail'))
    : null;

  if (kioskCampusSlug) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
        <div className="App">
          <title>GREATERBASE</title>
          <TimetablePage
            kiosk
            campusSlug={kioskCampusSlug}
            selectedDate={globalDate}
            setSelectedDate={setGlobalDate}
            selectedCampusId={globalCampusId}
            setSelectedCampusId={setGlobalCampusId}
            onLogout={handleLogout}
          />
        </div>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
      <div className="App">
        <title>GREATERBASE</title>
        <AppBar position="static">
          <Toolbar
            sx={{
              minHeight: { xs: 48, sm: 56, md: 64 },
              px: { xs: 1, sm: 2 },
            }}
          >
            {isAuthenticated && (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <FormControl
                  sx={{
                    m: 1,
                    minWidth: { xs: 80, sm: 120 },
                  }}
                  size="small"
                >
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    id="year-select"
                    value={selectedYear}
                    label="Year"
                    onChange={handleYearChange}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {isAuthenticated && (
              <>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: { xs: 0.25, sm: 0.5, md: 1 },
                    flexWrap: 'wrap',
                  }}
                >
                  <IconButton
                    edge="start"
                    color="inherit"
                    component="a"
                    href={viewHref('landing')}
                    onClick={(e) => {
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      handleGoHome();
                    }}
                    sx={{ mr: 2 }}
                  >
                    <HomeIcon />
                  </IconButton>

                  <Button
                    color="inherit"
                    component="a"
                    href={viewHref('students')}
                    onClick={(e) => {
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      switchView('students');
                    }}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                      px: { xs: 1, sm: 1.5 },
                      minWidth: 0,
                    }}
                  >
                    {isMobile ? "Stu" : "Students"}
                  </Button>

                  <Button
                    color="inherit"
                    component="a"
                    href={viewHref('adults')}
                    onClick={(e) => {
                      // The browser's own gestures — a new tab, a new window —
                      // are left to the browser; a plain click is the app.
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      switchView('adults');
                    }}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                      px: { xs: 1, sm: 1.5 },
                      minWidth: 0,
                    }}
                  >
                    {isMobile ? "Adt" : "Adults"}
                  </Button>

                  <Button
                    color="inherit"
                    component="a"
                    href={viewHref('groups')}
                    onClick={(e) => {
                      // The browser's own gestures — a new tab, a new window —
                      // are left to the browser; a plain click is the app.
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      switchView('groups');
                    }}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                      px: { xs: 1, sm: 1.5 },
                      minWidth: 0,
                    }}
                  >
                    {isMobile ? "Grp" : "Groups"}
                  </Button>

                  <Button
                    color="inherit"
                    component="a"
                    href={viewHref('timetable')}
                    onClick={(e) => {
                      // The browser's own gestures — a new tab, a new window —
                      // are left to the browser; a plain click is the app.
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      switchView('timetable');
                    }}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                      px: { xs: 1, sm: 1.5 },
                      minWidth: 0,
                    }}
                  >
                    {isMobile ? "Time" : "Timetable"}
                  </Button>

                  <IconButton
                    color="inherit"
                    component="a"
                    href={viewHref('help')}
                    onClick={(e) => {
                      if (!isPlainClick(e)) return;
                      e.preventDefault();
                      switchView('help');
                    }}
                    size="small"
                    title="Help"
                    aria-label="Help"
                  >
                    <HelpOutlineIcon />
                  </IconButton>

                  <IconButton
                    color="inherit"
                    onClick={handleRecentOpen}
                    size="small"
                    title="Account"
                  >
                    <AccountCircleIcon />
                  </IconButton>
                  <Menu
                    anchorEl={recentAnchorEl}
                    open={Boolean(recentAnchorEl)}
                    onClose={handleRecentClose}
                  >
                    {/* Who this tab is signed in as. Worth saying plainly:
                        one person may hold two accounts, and the level is
                        what decides half of what they can see. */}
                    <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {sessionStorage.getItem('userEmail') || 'Signed in'}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {[getRole(), ...getGrants().map((g) => g.charAt(0) + g.slice(1).toLowerCase())]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    </Box>

                    <Divider />

                    {sessionStorage.getItem('userEmail') !== RECOVERY_USER_EMAIL && (
                      <MenuItem
                        onClick={() => {
                          handleRecentClose();
                          setChangePasswordOpen(true);
                        }}
                      >
                        <ListItemText primary="Change password" />
                      </MenuItem>
                    )}

                    {sessionStorage.getItem('userEmail') !== RECOVERY_USER_EMAIL && (
                      <Divider />
                    )}

                    {recentStudents.length === 0 ? (
                      <MenuItem disabled>
                        <ListItemText primary="No recent students" />
                      </MenuItem>
                    ) : (
                      recentStudents.map((s) => (
                        <MenuItem
                          key={s.studentId}
                          onClick={() => handleRecentSelect(s.studentId)}
                        >
                          <ListItemText
                            primary={`${s.studentId} — ${s.studentSurname}, ${s.studentName}`}
                          />
                        </MenuItem>
                      ))
                    )}

                    <Divider />

                    <MenuItem
                      onClick={() => {
                        handleRecentClose();
                        handleLogout();
                      }}
                    >
                      <ListItemText primary="Logout" />
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            )}
          </Toolbar>
        </AppBar>

        {isAuthenticated && activeView === 'landing' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              mx: 'auto',
              mt: { xs: 0, sm: 8 },
              px: 2,
            }}
          >
            <Box sx={{ mb: 4, width: '100%' }}>
              <LandingPage onSelect={handleLandingSelect} />
            </Box>
          </Box>
        )}

        {isAuthenticated && activeView !== 'landing' && (
          activeView === 'residents' ? (
            <Residents />
          ) : activeView === 'rent' ? (
            <Rent selectedYear={selectedYear} />
          ) : activeView === 'transfers' ? (
            <TransfersPage
              onSelectStudent={handleSelectStudent}
              selectedYear={selectedYear}
              selectedDate={globalDate}
              setSelectedDate={setGlobalDate}
            />
          ) : activeView === 'classes' ? (
            <ClassesModule
              selectedYear={selectedYear}
              onSelectStudent={handleSelectStudent}
              selectedDate={globalDate}
              selectedCampusId={globalCampusId}
              setSelectedDate={setGlobalDate}
              setSelectedCampusId={setGlobalCampusId}
              initialTab={staffScheduleReturnView === 'classes' ? 'manage' : null}
              onOpenStaffSchedule={() => {
                setStaffScheduleReturnView('classes');
                setActiveView('staff_schedule');
              }}
            />
          ) : activeView === 'bedrooms' ? (
            <BedroomsModule
              mode="board"
              selectedYear={selectedYear}
              onSelectStudent={handleSelectStudent}
              onSelectAdult={handleSelectAdult}
              selectedDate={globalDate}
              selectedCampusId={globalCampusId}
              setSelectedDate={setGlobalDate}
              setSelectedCampusId={setGlobalCampusId}
              navGuardRef={bedroomsNavRef}
            />
          ) : activeView === 'admin' ? (
            <AdminPage selectedYear={selectedYear} />
          ) : activeView === 'agents' ? (
            <AgentsPage year={selectedYear} />
          ) : activeView === 'adults' ? (
            <AdultSearch
              selectedYear={selectedYear}
              showFinancials={showFinancials}
              openAdultId={selectedAdultId}
              clearOpenAdultId={() => setSelectedAdultId(null)}
            />
          ) : activeView === 'outstanding' ? (
            <OutstandingBalances
              selectedYear={selectedYear}
              onSelectStudent={handleSelectStudent}
            />
          ) : activeView === 'allergies' ? (
            <Allergies selectedYear={selectedYear} />
          ) : activeView === 'daily_summary' ? (
            <DailySummaryPage
              selectedDate={globalDate}
              selectedCampusId={globalCampusId}
            />
          ) : activeView === 'birthdays' ? (
            <BirthdayCalendarPage
              selectedYear={selectedYear}
              onSelectStudent={handleSelectStudent}
            />
          ) : activeView === 'activities' ? (
            <ActivitiesPage
              selectedDate={globalDate}
              setSelectedDate={setGlobalDate}
              selectedCampusId={globalCampusId}
              setSelectedCampusId={setGlobalCampusId}
              initialSlot={activitiesSlot}
              onOpenStaffSchedule={(date, campusId) =>
                handleOpenStaffSchedule(date, campusId, 'activities')
              }
            />
          ) : activeView === 'daily_rota' ? (
            <DailyRotaView
              selectedDate={globalDate}
              setSelectedDate={setGlobalDate}
              selectedCampusId={globalCampusId}
              setSelectedCampusId={setGlobalCampusId}
            />
          ) : activeView === 'hours' ? (
            // Reviewing hours and overtime needs ADMIN or the SUPERVISOR
            // grant; the server scopes the rows to the caller's list.
            can('supervisor') ? (
              <StaffHoursPage
                selectedDate={globalDate}
                selectedCampusId={globalCampusId}
              />
            ) : null
          ) : activeView === 'products' ? (
            showFinancials ? <ProductsPage selectedYear={selectedYear} /> : null
          ) : activeView === 'transport' ? (
            <TransportPage
              selectedDate={globalDate}
              setSelectedDate={setGlobalDate}
              selectedCampusId={globalCampusId}
              setSelectedCampusId={setGlobalCampusId}
            />
          ) : activeView === 'timetable' ? (
            <TimetablePage
              selectedYear={selectedYear}
              selectedDate={globalDate}
              setSelectedDate={setGlobalDate}
              selectedCampusId={globalCampusId}
              setSelectedCampusId={setGlobalCampusId}
              onOpenBedrooms={handleOpenBedrooms}
              onOpenTransfers={handleOpenTransfers}
              onOpenTransport={handleOpenTransport}
              onOpenClasses={handleOpenClasses}
              onOpenActivities={handleOpenActivities}
              onOpenStaffSchedule={handleOpenStaffSchedule}
            />
          ) : activeView === 'staff_schedule' ? (
            <StaffSchedulingView
              selectedDate={globalDate}
              selectedCampusId={globalCampusId}
              setSelectedCampusId={setGlobalCampusId}
              onSelectAdult={handleSelectAdult}
              onBack={
                staffScheduleReturnView
                  ? () => setActiveView(staffScheduleReturnView)
                  : null
              }
              backLabel={
                STAFF_SCHEDULE_BACK_LABELS[staffScheduleReturnView] || 'Back'
              }
            />
          ) : activeView === 'students' ? (
            <StudentSearch
              showFinancials={showFinancials}
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
              openStudentId={selectedStudentId}
              clearOpenStudentId={() => setSelectedStudentId(null)}
            />
          ) : activeView === 'groups' ? (
            <GroupsPage showFinancials={showFinancials} selectedYear={selectedYear} onSelectStudent={handleSelectStudent} />
          ) : activeView === 'group_gantt' ? (
            <BookingGanttPage
              selectedYear={selectedYear}
              showFinancials={showFinancials}
              onSelectStudent={handleSelectStudent}
            />
          ) : activeView === 'quotes' ? (
            showFinancials ? <QuotesPage showFinancials={showFinancials} /> : null
          ) : activeView === 'help' ? (
            <HelpPage initialSlug={helpSlug} />
          ) : null
        )}

        {!isAuthenticated && !restoring && (
          <Login onLoginSuccess={onLoginSuccess} />
        )}

        <>
          <VersionChecker />
        </>

        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          onChanged={() => {
            setChangePasswordOpen(false);
            handleLogout();
          }}
        />

        <Dialog
          open={Boolean(pendingNav)}
          onClose={() => setPendingNav(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Unsaved room changes</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You have unsaved changes in the bedroom planner. Save them before
              leaving, or discard them?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingNav(null)}>Cancel</Button>
            <Button onClick={handlePendingNavDiscard} color="error">
              Discard &amp; leave
            </Button>
            <Button onClick={handlePendingNavSave} variant="contained">
              Save &amp; leave
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
}

export default App;